import { IS_WEB } from 'constants/platform'
import { useTheme } from 'contexts'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ScrollView, StyleSheet } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import * as Yup from 'yup'

import { Avatar } from 'widgets/Avatar'
import { Box } from 'widgets/Box'
import { BorderlessField, Field } from 'widgets/Field'
import { AutoSaveForm, Form } from 'widgets/Form'
import { Icon } from 'widgets/Icon'
import { Pressable } from 'widgets/Pressable'

import { useClientSubscriptions } from 'hooks/useClientSubscriptions'
import { useCurrentUser } from 'hooks/useCurrentUser'
import { useGroup } from 'hooks/useGroup'
import { useGroups } from 'hooks/useGroups'
import { useMaxWidth } from 'hooks/useMaxWidth'
import { useMultipleUsers } from 'hooks/useMultipleUsers'

import { InProgressPost, InProgressPostStep } from 'types/post'

import { PostStepsForm } from './subcomponents/PostStepsForm'

type PostEditorFormProps = {
  postFormData: InProgressPost
  deleteStepAtIndex: (stepIndex: number, navigate?: boolean) => void
  saveDetails: (values: Partial<InProgressPost>) => void
  saveMultipleSteps: (stepsData: Partial<InProgressPostStep[]>) => void
}

const styles = StyleSheet.create({
  categoriesContainer: {
    overflow: IS_WEB ? 'hidden' : 'visible',
  },
})

export const PostEditorForm = ({
  postFormData,
  saveMultipleSteps,
  deleteStepAtIndex,
  saveDetails,
}: PostEditorFormProps) => {
  const theme = useTheme()
  const maxWidth = useMaxWidth()
  const insets = useSafeAreaInsets()

  const openImagePickerRef = useRef<any>(null)
  const textInputRef = useRef<any>(null)

  const { data: currentUser } = useCurrentUser()
  const { data: group, meta } = useGroup({ userId: currentUser?.id })
  const { hasSetupGroup } = meta

  const { data: subscriptions, isLoading: isSubscriptionsLoading } = useClientSubscriptions({
    userId: currentUser?.id,
  })

  const { data: groups, isLoading: isGroupsLoading } = useGroups(
    subscriptions?.map((sub) => sub.program.id ?? '') ?? [],
  )

  const { data: subscribedTrainers, isLoading: isSubscribedTrainersLoading } = useMultipleUsers({
    userIds: subscriptions?.map((sub) => sub.program.trainer.id ?? '') ?? [],
  })

  const [viewerCategories, setViewerCategories] = useState<{ value: string; label: string }[]>([
    {
      label: 'Draft',
      value: 'draft',
    },
    {
      label: 'Friends',
      value: 'friends',
    },
    {
      label: 'Followers',
      value: 'followers',
    },
  ])

  useEffect(() => {
    if (!hasSetupGroup) return
    setViewerCategories([
      ...viewerCategories,
      {
        label: group?.title ?? 'Your Group',
        value: 'upspace',
      },
    ])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSetupGroup])

  const memoizedViewerCategories = useMemo((): { value: string; label: string }[] => {
    if (isSubscriptionsLoading) return []
    const subscriptionsList: { label: string; value: string }[] = []
    if (groups && subscribedTrainers) {
      subscriptions?.forEach((sub, index) => {
        subscriptionsList.push({
          label:
            groups[index]?.title ??
            `${subscribedTrainers[index]?.firstName} ${subscribedTrainers[index]?.lastName}'s Group`,
          value: sub.program.id,
        })
      })
    }

    return subscriptionsList && subscriptionsList.length
      ? [...viewerCategories, ...subscriptionsList]
      : viewerCategories
    // There is an infinite loop with exhaustive deps on this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubscriptionsLoading, isGroupsLoading, isSubscribedTrainersLoading, viewerCategories])

  useEffect(() => {
    textInputRef?.current?.focus()
  }, [])

  return (
    <Box
      flex={1}
      style={{
        paddingBottom: insets.bottom,
      }}
    >
      <Box flex={1} padding={{ top: IS_WEB ? 'sm' : 'md' }}>
        <ScrollView>
          <Box testID='InputFormContainer'>
            <Box zIndex={9}>
              <Form
                initialValues={{ ...postFormData }}
                validationSchema={Yup.object().shape({
                  description: Yup.string().required('This field is required.'),
                })}
                onSubmit={() => {}}
              >
                {() => (
                  <>
                    <Box
                      testID='DropdownInputsContainer'
                      zIndex={99}
                      flexDirection='row'
                      margin={{ top: 'sm' }}
                    >
                      <Box
                        testID='AvatarContainer'
                        align='center'
                        justifyContent='center'
                        width={maxWidth.width * 0.13}
                        margin={{ right: 'md' }}
                      >
                        <Avatar
                          width={maxWidth.width * 0.13}
                          height={maxWidth.width * 0.13}
                          image={currentUser?.avatar}
                        />
                      </Box>
                      <Box
                        flex={1}
                        borderRadius='xs'
                        border={{ color: 'greyLight', width: 1 }}
                        style={styles.categoriesContainer}
                      >
                        <BorderlessField
                          key={memoizedViewerCategories.length}
                          multiSelect
                          type='select'
                          options={memoizedViewerCategories}
                          isSearchable={false}
                          placeholder='Where to post'
                          name='combinedCategoriesAndProgramIds'
                        />
                      </Box>
                    </Box>

                    <Box testID='PostDescriptionContainer' flexDirection='row'>
                      <Box width={maxWidth.width * 0.13} margin={{ right: 'sm' }} />
                      <Box flex={1} margin={{ top: IS_WEB ? 12 : 0, bottom: IS_WEB ? 20 : 0 }}>
                        <Field
                          ref={textInputRef}
                          required
                          type='textarea'
                          name='description'
                          placeholderTextColor='grey'
                          placeholder='What would you like to share?'
                          style={{
                            color: theme.colors.black,
                            backgroundColor: theme.colors.transparent,
                          }}
                        />
                      </Box>
                    </Box>

                    <AutoSaveForm onSave={saveDetails} debounce={1000} />
                  </>
                )}
              </Form>
            </Box>

            <Box style={!postFormData?.steps[0]?.file ? { display: 'none' } : {}}>
              <PostStepsForm
                ref={openImagePickerRef}
                initialValues={postFormData}
                onValuesChange={saveDetails}
                saveMultipleSteps={saveMultipleSteps}
                deleteStepAtIndex={deleteStepAtIndex}
                paddingOnTop={false}
                mediaSize='twoThird'
              />
            </Box>
          </Box>
        </ScrollView>
      </Box>

      <Box
        testID='ImagePickerButtonsContainer'
        width='100%'
        flexDirection='row'
        align='center'
        style={postFormData?.steps[0]?.file ? { display: 'none' } : {}}
        margin={IS_WEB ? 'md' : { horizontal: 'md' }}
      >
        <Pressable
          onPress={() => {
            openImagePickerRef?.current?.openFileUpload()
          }}
        >
          <Box>
            <Icon name='image' color='greyDark' />
          </Box>
        </Pressable>
        {/* To be fixed in FIT-512 */}
        {/* {!IS_WEB && (
              <Pressable
                onPress={() => {
                  openImagePickerRef?.current?.openCameraUpload()
                }}
              >
                <Box margin={{ left: 'md' }}>
                  <Icon name='camera' color='greyDark' />
                </Box>
              </Pressable>
            )} */}
      </Box>
    </Box>
  )
}
